import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Lesson = {
  id: string;
  section_id: string;
  subject_id: string;
  staff_link_id?: string | null;
  room_id?: string | null;
  day_of_week: number;
  period_id: string;
  is_locked?: number | boolean;
  source?: string;
};

type Scenario = {
  id: string;
  name: string;
  status: string;
  objective_total?: number | null;
  hard_conflict_count?: number;
  lessons?: Lesson[];
  scores?: Array<{ metric_code: string; metric_label: string; metric_value: number }>;
  diagnostics?: Array<{ constraint_code: string; severity: string; message: string }>;
};

export default function SmartTimetableScenarioDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [explainForm] = Form.useForm();
  const [moveForm] = Form.useForm();

  const query = useQuery({
    queryKey: ['education-smart-tt-scenario', id],
    enabled: Boolean(id),
    queryFn: () => api.get<Scenario>(`/education/timetable/smart/scenarios/${id}`),
  });
  const scenario = query.data?.data;

  const publish = useMutation({
    mutationFn: () => api.post(`/education/timetable/smart/scenarios/${id}/publish`, { publish_grids: true }),
    onSuccess: () => {
      message.success(t('scenarioPublished', { defaultValue: 'Scenario published into timetable grids' }));
      queryClient.invalidateQueries({ queryKey: ['education-smart-tt-scenario', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const clone = useMutation({
    mutationFn: () => api.post<Scenario>(`/education/timetable/smart/scenarios/${id}/clone`, {}),
    onSuccess: (result) => {
      message.success(t('scenarioCloned', { defaultValue: 'What-if clone created' }));
      const cloneId = result.data?.id;
      if (cloneId) navigate(`/education/timetable/smart/scenarios/${cloneId}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const reoptimize = useMutation({
    mutationFn: () =>
      api.post<{ id: string; locked_lesson_count?: number }>(
        `/education/timetable/smart/scenarios/${id}/reoptimize`,
        { candidate_count: 1, time_budget_sec: 30 }
      ),
    onSuccess: (result) => {
      message.success(
        t('reoptimizeQueued', {
          defaultValue: 'Partial regenerate queued ({{locked}} locked lessons preserved)',
          locked: result.data?.locked_lesson_count ?? 0,
        })
      );
      if (result.data?.id) navigate(`/education/timetable/smart/runs/${result.data.id}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const lock = useMutation({
    mutationFn: (lessonId: string) =>
      api.post(`/education/timetable/smart/scenarios/${id}/lessons/${lessonId}/lock`, { reason: 'Planner lock' }),
    onSuccess: () => {
      message.success(t('locked', { defaultValue: 'Locked' }));
      queryClient.invalidateQueries({ queryKey: ['education-smart-tt-scenario', id] });
    },
  });

  const moveLesson = useMutation({
    mutationFn: (values: { lesson_id: string; day_of_week: number; period_id: string }) =>
      api.patch(`/education/timetable/smart/scenarios/${id}/lessons/move`, values),
    onSuccess: () => {
      message.success(t('lessonMoved', { defaultValue: 'Lesson moved' }));
      moveForm.resetFields(['day_of_week', 'period_id']);
      queryClient.invalidateQueries({ queryKey: ['education-smart-tt-scenario', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const explain = useMutation({
    mutationFn: (values: { question: string }) =>
      api.post<{ answer: string }>('/education/timetable/smart/explain', {
        scenario_id: id,
        question: values.question,
      }),
    onSuccess: (result) => {
      message.info(result.data?.answer || '');
    },
  });

  const canPublish = scenario?.status === 'validated' && Number(scenario?.hard_conflict_count || 0) === 0;
  const canEdit = scenario?.status !== 'published';
  const unlockedLessons = (scenario?.lessons || []).filter((lesson) => !lesson.is_locked);
  const periodOptions = [...new Set((scenario?.lessons || []).map((lesson) => lesson.period_id))].map((periodId) => ({
    value: periodId,
    label: periodId,
  }));

  return (
    <EducationPageChrome
      title={scenario?.name || t('scenario', { defaultValue: 'Scenario' })}
      description={t('scenarioDetailIntro', {
        defaultValue: 'Inspect lessons, scores and hard-constraint diagnostics. Publish uses existing draft→publish lifecycle.',
      })}
      tags={scenario ? <Tag color={scenario.status === 'validated' ? 'green' : 'default'}>{scenario.status}</Tag> : undefined}
      extra={
        <Space wrap>
          <Button onClick={() => navigate('/education/timetable/smart/scenarios')}>{t('back', { defaultValue: 'Back' })}</Button>
          <Button loading={clone.isPending} onClick={() => clone.mutate()}>
            {t('cloneWhatIf', { defaultValue: 'Clone what-if' })}
          </Button>
          <Button disabled={!canEdit} loading={reoptimize.isPending} onClick={() => reoptimize.mutate()}>
            {t('reoptimize', { defaultValue: 'Reoptimize' })}
          </Button>
          <Popconfirm
            title={t('publishScenarioConfirm', {
              defaultValue: 'Publish this scenario into section grids and mark grids published?',
            })}
            disabled={!canPublish}
            onConfirm={() => publish.mutate()}
          >
            <Button type="primary" disabled={!canPublish} loading={publish.isPending}>
              {t('publish', { defaultValue: 'Publish' })}
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {!canPublish ? (
          <Alert
            type="warning"
            showIcon
            message={t('cannotPublishInvalid', {
              defaultValue: 'Scenario has hard conflicts or is not validated — publish blocked.',
            })}
          />
        ) : null}

        <Card size="small" title={t('scores', { defaultValue: 'Objective scores' })} loading={query.isLoading}>
          <Descriptions size="small" column={1}>
            {(scenario?.scores || []).map((score) => (
              <Descriptions.Item key={score.metric_code} label={score.metric_label}>
                {score.metric_value}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Card>

        <Card size="small" title={t('diagnostics', { defaultValue: 'Diagnostics' })}>
          <Table
            rowKey={(_, index) => String(index)}
            size="small"
            pagination={false}
            dataSource={scenario?.diagnostics || []}
            locale={{ emptyText: t('noDiagnostics', { defaultValue: 'No diagnostics' }) }}
            columns={[
              { title: t('code', { defaultValue: 'Code' }), dataIndex: 'constraint_code', width: 220 },
              {
                title: t('severity', { defaultValue: 'Severity' }),
                dataIndex: 'severity',
                width: 100,
                render: (value: string) => <Tag color={value === 'hard' ? 'red' : 'gold'}>{value}</Tag>,
              },
              { title: t('message', { defaultValue: 'Message' }), dataIndex: 'message' },
            ]}
          />
        </Card>

        <Card size="small" title={t('lessons', { defaultValue: 'Lessons' })}>
          <Table
            rowKey="id"
            size="small"
            dataSource={scenario?.lessons || []}
            pagination={{ pageSize: 20 }}
            columns={[
              { title: t('section'), dataIndex: 'section_id' },
              { title: t('subject'), dataIndex: 'subject_id' },
              { title: t('day', { defaultValue: 'Day' }), dataIndex: 'day_of_week', width: 70 },
              { title: t('period'), dataIndex: 'period_id' },
              { title: t('teacher'), dataIndex: 'staff_link_id' },
              { title: t('room'), dataIndex: 'room_id' },
              {
                title: t('locked', { defaultValue: 'Locked' }),
                dataIndex: 'is_locked',
                width: 90,
                render: (value: number | boolean) => (value ? <Tag color="blue">locked</Tag> : '—'),
              },
              {
                title: t('actions'),
                render: (_: unknown, row: Lesson) =>
                  canEdit && !row.is_locked ? (
                    <Button size="small" onClick={() => lock.mutate(row.id)}>
                      {t('lock', { defaultValue: 'Lock' })}
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>

        {canEdit ? (
          <Card size="small" title={t('moveLesson', { defaultValue: 'Move lesson' })}>
            <Form
              form={moveForm}
              layout="vertical"
              onFinish={(values) => moveLesson.mutate(values)}
              style={{ maxWidth: 640 }}
            >
              <Form.Item
                name="lesson_id"
                label={t('lesson', { defaultValue: 'Lesson' })}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={unlockedLessons.map((lesson) => ({
                    value: lesson.id,
                    label: `${lesson.subject_id} · day ${lesson.day_of_week} · ${lesson.period_id}`,
                  }))}
                  placeholder={t('selectLesson', { defaultValue: 'Select unlocked lesson' })}
                />
              </Form.Item>
              <Form.Item
                name="day_of_week"
                label={t('day', { defaultValue: 'Day' })}
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={7} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="period_id"
                label={t('period')}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  options={periodOptions}
                  placeholder={t('selectPeriod', { defaultValue: 'Select period' })}
                />
              </Form.Item>
              <Button htmlType="submit" loading={moveLesson.isPending} disabled={!unlockedLessons.length}>
                {t('move', { defaultValue: 'Move' })}
              </Button>
            </Form>
          </Card>
        ) : null}

        <Card size="small" title={t('aiExplain', { defaultValue: 'Deterministic explanation' })}>
          <Form form={explainForm} layout="vertical" onFinish={(values) => explain.mutate(values)} style={{ maxWidth: 640 }}>
            <Form.Item name="question" label={t('question', { defaultValue: 'Question' })} rules={[{ required: true }]}>
              <Input.TextArea rows={2} placeholder="Why is this scenario invalid? Which teachers are overloaded?" />
            </Form.Item>
            <Button htmlType="submit" loading={explain.isPending}>
              {t('explain', { defaultValue: 'Explain' })}
            </Button>
          </Form>
          {explain.data?.data?.answer ? (
            <Alert style={{ marginTop: 12 }} type="info" showIcon message={explain.data.data.answer} />
          ) : null}
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
